/*
 * <cart-extras>
 * Handles the "+" add buttons inside the cart-drawer Add Extras upsell.
 * Adds the variant, then refreshes the drawer sections (items, footer, bubbles)
 * and itself via the Section Rendering API — reusing the same flow as product-form.js.
 */
if (!customElements.get('cart-extras')) {
  customElements.define(
    'cart-extras',
    class CartExtras extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('input', this.onInput.bind(this));
      }

      connectedCallback() {
        this.buildFilters();
        // Refresh the offer list whenever the cart changes elsewhere (product page,
        // quantity change, remove) so purchased items drop out of the offers.
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === 'cart-extras') return; // our own adds already re-render
          this.refresh();
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      refresh() {
        fetch(`${this.cartUrl}?section_id=cart-add-extras`)
          .then((response) => response.text())
          .then((html) => {
            this.innerHTML = this.getSectionInnerHTML(html, '.js-cart-extras-contents');
            this.buildFilters();
          })
          .catch((error) => console.error(error));
      }

      // A tab holding more than one kind of extra (cards, coffee, gift wrap, caps)
      // gets filter pills, built from the groups actually present so nothing has
      // to be configured. One kind only: no pills.
      buildFilters() {
        this.querySelectorAll('.cart-extras__pane').forEach((pane) => {
          pane.querySelector('.cart-extras__filters')?.remove();
          const items = [...pane.querySelectorAll('.cart-extras__item-wrap')];
          const groups = [...new Set(items.map((i) => i.querySelector('[data-group]')?.dataset.group).filter(Boolean))];
          if (groups.length < 2) return;

          const row = document.createElement('div');
          row.className = 'cart-extras__filters';
          row.setAttribute('role', 'group');
          row.setAttribute('aria-label', 'Filter extras');
          row.innerHTML = ['All', ...groups]
            .map((g, i) => `<button type="button" class="cart-extras__filter${i === 0 ? ' active' : ''}" data-group-filter="${g}" aria-pressed="${i === 0}">${g}</button>`)
            .join('');
          pane.querySelector('.cart-extras__list').before(row);
        });
      }

      applyFilter(button) {
        const pane = button.closest('.cart-extras__pane');
        const group = button.dataset.groupFilter;
        pane.querySelectorAll('[data-group-filter]').forEach((b) => {
          const on = b === button;
          b.classList.toggle('active', on);
          b.setAttribute('aria-pressed', String(on));
        });
        pane.querySelectorAll('.cart-extras__item-wrap').forEach((item) => {
          const itemGroup = item.querySelector('[data-group]')?.dataset.group;
          item.hidden = group !== 'All' && itemGroup !== group;
        });
      }

      get cartDrawer() {
        return document.getElementById('cart-drawer');
      }

      // This theme's window.routes has no cart_url; derive it from cart_add_url
      // (preserves any locale prefix, e.g. /en-gb/cart).
      get cartUrl() {
        return routes.cart_add_url.replace(/\/add(\.js)?$/, '');
      }

      onClick(event) {
        const filter = event.target.closest('[data-group-filter]');
        if (filter) {
          event.preventDefault();
          this.applyFilter(filter);
          return;
        }

        const toggle = event.target.closest('[data-card-toggle]');
        if (toggle) {
          event.preventDefault();
          this.toggleCardPanel(toggle);
          return;
        }

        const cardAdd = event.target.closest('[data-card-add]');
        if (cardAdd) {
          event.preventDefault();
          this.addCard(cardAdd);
          return;
        }

        const button = event.target.closest('.cart-extras__add[data-variant-id]');
        if (!button) return;
        event.preventDefault();
        this.addVariant(button);
      }

      // Greeting cards: "+" opens the message panel under the row. Only one panel
      // is open at a time, since only one card can go in the basket.
      toggleCardPanel(toggle) {
        const panel = document.getElementById(toggle.getAttribute('aria-controls'));
        if (!panel) return;
        const opening = panel.hidden;

        this.querySelectorAll('[data-card-panel]').forEach((other) => {
          if (other === panel) return;
          other.hidden = true;
          const otherToggle = this.querySelector(`[aria-controls="${other.id}"]`);
          if (otherToggle) otherToggle.setAttribute('aria-expanded', 'false');
        });

        panel.hidden = !opening;
        toggle.setAttribute('aria-expanded', String(opening));
        if (opening) panel.querySelector('[data-card-input]').focus();
      }

      onInput(event) {
        const input = event.target.closest('[data-card-input]');
        if (!input) return;
        const panel = input.closest('[data-card-panel]');
        window.B22CardMessage.stripEmoji(input);
        panel.querySelector('[data-card-counter]').textContent = `${input.value.length} / ${input.maxLength}`;
        if (input.value.trim() !== '') {
          input.removeAttribute('aria-invalid');
          panel.querySelector('[data-card-error]').hidden = true;
        }
      }

      addCard(button) {
        const panel = button.closest('[data-card-panel]');
        const input = panel.querySelector('[data-card-input]');
        const message = input.value.replace(/ {2,}/g, ' ').trim();
        if (message === '') {
          input.setAttribute('aria-invalid', 'true');
          panel.querySelector('[data-card-error]').hidden = false;
          input.focus();
          return;
        }
        this.addVariant(button, { 'Card message': message });
      }

      addVariant(button, properties) {
        if (button.classList.contains('loading')) return;
        button.classList.add('loading');
        button.setAttribute('aria-disabled', 'true');

        const sections = this.sectionsToRender();
        // The Section Rendering API allows max 5 sections per request; drop the
        // non-visual a11y live-region to stay within the limit.
        const sectionIds = sections
          .map((section) => section.id)
          .filter((id) => id !== 'cart-live-region-text')
          .slice(0, 5)
          .join(',');
        const config = fetchConfig();
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        const item = { id: button.dataset.variantId, quantity: 1 };
        if (properties) item.properties = properties;
        config.body = JSON.stringify(item);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              console.error('cart-extras add failed:', response.description || response.message);
              return null;
            }
            // Re-render from a FRESH cart render. The sections returned inline by
            // /cart/add.js can be stale when an automatic discount recalculates
            // (e.g. the 20%-off-2nd-box), which left the checkout total pre-add.
            return fetch(`${this.cartUrl}?sections=${encodeURIComponent(sectionIds)}`).then((r) => r.json());
          })
          .then((freshSections) => {
            if (!freshSections) return;
            this.renderSections(freshSections, sections);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-extras',
              productVariantId: button.dataset.variantId,
            });
          })
          .catch((error) => console.error(error))
          .finally(() => {
            button.classList.remove('loading');
            button.removeAttribute('aria-disabled');
          });
      }

      sectionsToRender() {
        const drawer = this.cartDrawer;
        const sections = drawer ? [...drawer.getSectionsToRender()] : [];

        // Include this component so purchased items drop out of the offer list.
        if (!sections.some((section) => section.id === 'cart-add-extras')) {
          sections.push({
            id: 'cart-add-extras',
            section: 'cart-add-extras',
            selector: '.js-cart-extras-contents',
          });
        }

        return sections;
      }

      renderSections(responseSections, sections) {
        if (!responseSections) return;

        sections.forEach((section) => {
          const target = document.getElementById(section.id);
          const html = responseSections[section.id];
          if (!target || !html) return;
          target.innerHTML = this.getSectionInnerHTML(html, section.selector);
        });
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        return parsed ? parsed.innerHTML : '';
      }
    }
  );
}
